import React from 'react';
import { Modal, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from './icons';
import { Button } from './Button';
import { horsFrancais, t } from '../i18n';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';

const BLOCS = [
  { cle: 'offres', icone: 'briefcase-outline' },
  { cle: 'voix', icone: 'mic-outline' },
  { cle: 'langues', icone: 'language-outline' },
] as const;

/**
 * Nouveautés de la version, montrées une seule fois après une mise à jour (`services/nouveautes.ts`).
 *
 * Feuille glissante sur iOS : le geste vers le bas la ferme, comme le bouton, et `onRequestClose`
 * reçoit les deux — ainsi que le retour Android. Le contenu défile : avec un texte agrandi, le
 * bouton reste atteignable sous la liste.
 */
export function NouveautesModal({
  visible,
  onFermer,
  onVoirOffres,
  versionAnnoncee = true,
  politiqueAnnoncee = false,
  onVoirPolitique,
}: {
  visible: boolean;
  onFermer: () => void;
  /** Ferme la fenêtre et ouvre l'onglet Offres. Absent : pas de lien. */
  onVoirOffres?: () => void;
  /** Blocs de la version installée. */
  versionAnnoncee?: boolean;
  /** Bloc de la politique de confidentialité 1.12 (`politiqueAAnnoncer`). */
  politiqueAnnoncee?: boolean;
  /** Ferme la fenêtre et ouvre la politique. Absent : pas de lien. */
  onVoirPolitique?: () => void;
}) {
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle={Platform.OS === 'ios' ? 'pageSheet' : undefined}
      onRequestClose={onFermer}
    >
      {/* Racine modale pour le lecteur d'écran : sur la seule liste, le bouton deviendrait inatteignable. */}
      <SafeAreaView style={styles.fond} edges={['bottom']} testID="nouveautes" accessibilityViewIsModal>
        <ScrollView contentContainerStyle={styles.contenu}>
          <Text style={styles.titre} accessibilityRole="header">
            {t('nouveautes.titre')}
          </Text>
          {(versionAnnoncee ? BLOCS : []).map(({ cle, icone }) => (
            <View key={cle} style={styles.bloc} testID={`nouveautes-${cle}`}>
              <View style={styles.pastille} importantForAccessibility="no-hide-descendants" accessibilityElementsHidden>
                <Ionicons name={icone} size={24} color={colors.electric} />
              </View>
              <View style={styles.textes}>
                <Text style={styles.blocTitre} accessibilityRole="header">
                  {t(`nouveautes.${cle}Titre`)}
                </Text>
                <Text style={styles.blocTexte}>{t(`nouveautes.${cle}Texte`)}</Text>
                {cle === 'offres' && horsFrancais() ? (
                  <Text style={styles.blocTexte} testID="nouveautes-france-uniquement">
                    {t('offres.franceUniquement')}
                  </Text>
                ) : null}
                {cle === 'offres' && onVoirOffres ? (
                  <Pressable accessibilityRole="link" onPress={onVoirOffres} hitSlop={8} testID="nouveautes-voir-offres" style={styles.lien}>
                    <Text style={styles.lienTexte}>{t('nouveautes.voirOffres')}</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          ))}
          {politiqueAnnoncee ? (
            <View style={styles.bloc} testID="nouveautes-politique">
              <View style={styles.pastille} importantForAccessibility="no-hide-descendants" accessibilityElementsHidden>
                <Ionicons name="shield-checkmark-outline" size={24} color={colors.electric} />
              </View>
              <View style={styles.textes}>
                <Text style={styles.blocTitre} accessibilityRole="header">
                  {t('nouveautes.politiqueTitre')}
                </Text>
                <Text style={styles.blocTexte}>{t('nouveautes.politiqueTexte')}</Text>
                {horsFrancais() ? (
                  <Text style={styles.blocTexte} testID="nouveautes-politique-documents-fr">
                    {t('legalHeaders.documentsEnFrancais')}
                  </Text>
                ) : null}
                {onVoirPolitique ? (
                  <Pressable accessibilityRole="link" onPress={onVoirPolitique} hitSlop={8} testID="nouveautes-voir-politique" style={styles.lien}>
                    <Text style={styles.lienTexte}>{t('nouveautes.voirPolitique')}</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          ) : null}
        </ScrollView>
        <View style={styles.pied}>
          <Button title={t('nouveautes.bouton')} variant="dark" onPress={onFermer} testID="nouveautes-fermer" />
        </View>
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  fond: { flex: 1, backgroundColor: colors.grey50 },
  contenu: { padding: screenPadding, paddingTop: spacing.xxl, gap: spacing.lg },
  titre: {
    fontSize: typography.display.size,
    lineHeight: typography.display.lineHeight,
    fontWeight: typography.display.weight,
    color: colors.navy,
  },
  bloc: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
    padding: spacing.lg,
    borderRadius: radius.lg,
    backgroundColor: colors.white,
  },
  pastille: {
    width: 44,
    height: 44,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.grey50,
  },
  textes: { flex: 1, gap: spacing.xs },
  blocTitre: { fontSize: typography.h3.size, lineHeight: typography.h3.lineHeight, fontWeight: typography.h3.weight, color: colors.night },
  blocTexte: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  lien: { alignSelf: 'flex-start', minHeight: 44, justifyContent: 'center' },
  lienTexte: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.electric, textDecorationLine: 'underline' },
  pied: { paddingHorizontal: screenPadding, paddingVertical: spacing.md },
});
